import type { Substance } from '../types';

export const fluoxetine: Substance = {
  id: 'fluoxetine',
  displayName: 'Fluoxetine (SSRI)',
  emoji: '🧩',
  aliases: ['fluoxetine', 'prozac', 'ssri'],
  substanceClass: 'medicine',
  mechanismSummary:
    'Selectively blocks the serotonin transporter, so released serotonin lingers in the synapse. ' +
    'Transporter blockade is immediate, yet the clinical antidepressant effect takes weeks — strong ' +
    'evidence that the therapeutic mechanism involves downstream adaptation, not the blockade itself. ' +
    'SERT is also dense in the gut, which is why nausea is the most common early side effect.',
  pk: {
    route: 'oral',
    ka: 0.6,
    ke: 0.0116,
    vd: 2500,
    bioavailability: 0.72,
    molecularWeightGMol: 309.33,
    brainPlasmaRatio: 2.0,
  },
  doseRange: { min: 10, max: 60, default: 20, step: 10, unit: 'mg', displayScale: 1 },
  bindings: [
    {
      receptor: 'SERT',
      kiNM: 0.81,
      action: 'reuptake-inhibitor',
      citation:
        'Sub-nanomolar to low-nanomolar affinity, highly selective over NET and DAT. Owens MJ et al. ' +
        '(1997) J Pharmacol Exp Ther 283:1305-1322.',
    },
    {
      receptor: '5HT2C',
      kiNM: 250,
      action: 'antagonist',
      citation: 'Secondary activity; approximate. NIMH PDSP Ki Database.',
    },
  ],
  colorPalette: { primary: '#66bbdd', secondary: '#4499bb', particleSpeedMultiplier: 1.0 },
};

export const haloperidol: Substance = {
  id: 'haloperidol',
  displayName: 'Haloperidol',
  emoji: '🔷',
  aliases: ['haloperidol', 'haldol', 'antipsychotic'],
  substanceClass: 'medicine',
  mechanismSummary:
    'A high-affinity D2 antagonist and the classic "typical" antipsychotic. Blocking D2 in the limbic ' +
    'system reduces psychosis, but the same blockade in the striatum impairs movement — the origin of the ' +
    'motor side effects that define this drug class. Effectively the mirror image of a dopamine agonist.',
  pk: {
    route: 'oral',
    ka: 1.0,
    ke: 0.0385,
    vd: 1400,
    bioavailability: 0.6,
    molecularWeightGMol: 375.86,
    brainPlasmaRatio: 1.8,
  },
  doseRange: { min: 0.5, max: 15, default: 5, step: 0.5, unit: 'mg', displayScale: 1 },
  bindings: [
    {
      receptor: 'D2',
      kiNM: 1.2,
      action: 'antagonist',
      citation:
        'High-affinity D2 antagonism is haloperidol\'s defining property; sub-nanomolar to low-nanomolar ' +
        'values widely reported. NIMH PDSP Ki Database, https://pdsp.unc.edu/databases/kidb.php.',
    },
    {
      receptor: 'D1',
      kiNM: 120,
      action: 'antagonist',
      citation: 'Approximate; ~100x weaker than at D2. NIMH PDSP Ki Database.',
    },
    {
      receptor: 'ALPHA1',
      kiNM: 12,
      action: 'antagonist',
      citation:
        'Alpha-1 blockade explains the orthostatic hypotension seen clinically. Approximate; PDSP Ki Database.',
    },
  ],
  colorPalette: { primary: '#5577cc', secondary: '#334488', particleSpeedMultiplier: 0.6 },
};

export const lidocaine: Substance = {
  id: 'lidocaine',
  displayName: 'Lidocaine',
  emoji: '💉',
  aliases: ['lidocaine', 'lignocaine', 'local anesthetic', 'local anaesthetic'],
  substanceClass: 'medicine',
  mechanismSummary:
    'A local anaesthetic: it blocks voltage-gated sodium channels, so affected nerves cannot fire an action ' +
    'potential at all. Note where the effect sits — almost entirely in PERIPHERAL sensory nerves, not the ' +
    'brain. It does not alter mood or consciousness; it simply stops the signal reaching the spinal cord.',
  pk: {
    route: 'oral',
    ka: 2.4,
    ke: 0.4331,
    vd: 91,
    bioavailability: 0.35,
    molecularWeightGMol: 234.34,
    brainPlasmaRatio: 1.0,
  },
  doseRange: { min: 20, max: 200, default: 100, step: 10, unit: 'mg', displayScale: 1 },
  bindings: [
    {
      receptor: 'NAV',
      kiNM: 25000,
      action: 'antagonist',
      citation:
        'Functional sodium-channel block in the low micromolar range, state-dependent rather than a fixed ' +
        'Ki — affinity is much higher for inactivated channels. Order-of-magnitude estimate; see Catterall ' +
        'WA (2012) J Physiol 590:2577-2589.',
    },
  ],
  colorPalette: { primary: '#88ddcc', secondary: '#55aa99', particleSpeedMultiplier: 0.35 },
};

export const atropine: Substance = {
  id: 'atropine',
  displayName: 'Atropine',
  emoji: '🌱',
  aliases: ['atropine', 'belladonna', 'anticholinergic'],
  substanceClass: 'medicine',
  mechanismSummary:
    'A muscarinic acetylcholine antagonist that blocks parasympathetic signalling throughout the body. ' +
    'Because it blocks the vagus nerve\'s action on the heart, it speeds the heart up — the classic ' +
    'emergency use for dangerous bradycardia. Dilated pupils, dry mouth and gut slowing all follow from ' +
    'the same peripheral mechanism.',
  pk: {
    route: 'oral',
    ka: 2.0,
    ke: 0.1733,
    vd: 140,
    bioavailability: 0.25,
    molecularWeightGMol: 289.37,
    brainPlasmaRatio: 0.8,
  },
  doseRange: { min: 0.25, max: 3, default: 1, step: 0.25, unit: 'mg', displayScale: 1 },
  bindings: [
    {
      receptor: 'M2',
      kiNM: 1.1,
      action: 'antagonist',
      citation:
        'Atropine is a potent, essentially non-selective muscarinic antagonist with low-nanomolar affinity ' +
        'across M1-M5. Approximate; NIMH PDSP Ki Database.',
    },
    {
      receptor: 'M1',
      kiNM: 1.0,
      action: 'antagonist',
      citation: 'Approximate; non-selective across muscarinic subtypes. NIMH PDSP Ki Database.',
    },
  ],
  colorPalette: { primary: '#ddbb66', secondary: '#aa8844', particleSpeedMultiplier: 1.2 },
};

export const diphenhydramine: Substance = {
  id: 'diphenhydramine',
  displayName: 'Diphenhydramine',
  emoji: '😴',
  aliases: ['diphenhydramine', 'benadryl', 'antihistamine'],
  substanceClass: 'medicine',
  mechanismSummary:
    'A first-generation antihistamine. It blocks peripheral H1 receptors for allergy relief, but unlike ' +
    'newer antihistamines it crosses the blood-brain barrier freely and blocks H1 in the brain too — where ' +
    'histamine is a wakefulness transmitter. That crossing is the entire reason it makes you drowsy.',
  pk: {
    route: 'oral',
    ka: 1.6,
    ke: 0.0788,
    vd: 340,
    bioavailability: 0.43,
    molecularWeightGMol: 255.35,
    brainPlasmaRatio: 1.5,
  },
  doseRange: { min: 12.5, max: 100, default: 25, step: 12.5, unit: 'mg', displayScale: 1 },
  bindings: [
    {
      receptor: 'H1',
      kiNM: 16,
      action: 'antagonist',
      citation: 'Approximate; low-nanomolar H1 affinity widely reported. NIMH PDSP Ki Database.',
    },
    {
      receptor: 'M1',
      kiNM: 80,
      action: 'antagonist',
      citation:
        'Anticholinergic side activity explains dry mouth and confusion, especially in older adults. ' +
        'Approximate; NIMH PDSP Ki Database.',
    },
  ],
  colorPalette: { primary: '#99aacc', secondary: '#667799', particleSpeedMultiplier: 0.55 },
};

export const propranolol: Substance = {
  id: 'propranolol',
  displayName: 'Propranolol',
  emoji: '💓',
  aliases: ['propranolol', 'beta blocker', 'beta-blocker'],
  substanceClass: 'medicine',
  mechanismSummary:
    'A non-selective beta-adrenergic antagonist. By blocking β1 in the heart it blunts the physical side of ' +
    'the sympathetic response — racing heart, tremor — which is why it is used for performance anxiety. It ' +
    'largely treats the body\'s side of fear rather than the brain\'s, a neat illustration of how much of ' +
    '"anxiety" is peripheral.',
  pk: {
    route: 'oral',
    ka: 1.9,
    ke: 0.1733,
    vd: 280,
    bioavailability: 0.26,
    molecularWeightGMol: 259.34,
    brainPlasmaRatio: 1.2,
  },
  doseRange: { min: 10, max: 160, default: 40, step: 10, unit: 'mg', displayScale: 1 },
  bindings: [
    {
      receptor: 'BETA1',
      kiNM: 1.8,
      action: 'antagonist',
      citation: 'Approximate; low-nanomolar, non-selective across beta subtypes. NIMH PDSP Ki Database.',
    },
    {
      receptor: 'BETA2',
      kiNM: 1.5,
      action: 'antagonist',
      citation:
        'Non-selectivity at beta-2 is why propranolol is avoided in asthma — it can constrict airways. ' +
        'Approximate; NIMH PDSP Ki Database.',
    },
  ],
  colorPalette: { primary: '#dd6688', secondary: '#aa4466', particleSpeedMultiplier: 0.7 },
};

export const MEDICINES = [
  fluoxetine,
  haloperidol,
  lidocaine,
  atropine,
  diphenhydramine,
  propranolol,
];
